import { useRef } from 'react';
import type { ProjectDetail } from '../../lib/projectsApi';
import { TasksView } from '../tasks/TasksView';

/**
 * CLE-197 — the project's Tasks tab now renders the exact same Linear-style
 * experience as the global Tasks page, locked to this project. List + board +
 * filters + bulk actions + quick-add + drag-and-drop + task detail all come
 * from the shared <TasksView />; the project filter dropdown is hidden and the
 * per-task project name is omitted because the tab is already scoped to one
 * project.
 */
export function ProjectTasksTab({ project }: { project: ProjectDetail }) {
  // The tab reads filters from the URL exactly like the global page, so
  // ?tab=tasks&dueTo=2026-09-07 narrows the same way /tasks?dueTo=2026-09-07
  // does. Silently dropping them made the two screens contradict each other:
  // the tab looked like it held tasks the global list was hiding, when in
  // truth only the global list was filtering at all.
  //
  // The CLE-197 edge case still holds. Only the first project this tab renders
  // trusts the URL; navigating to another project keeps this component mounted
  // (the route param changes, the element does not), so the ref survives, the
  // seed turns off, and the remounted view starts clean instead of inheriting
  // the previous project's filters.
  const deepLinkedProject = useRef(project.id);

  // Key by project id so switching projects remounts the view: view mode is
  // persisted (localStorage) and survives, while in-component filter state
  // resets to a clean slate per project.
  return (
    <TasksView
      key={project.id}
      projectId={project.id}
      hideProjectFilter
      seedFromUrl={deepLinkedProject.current === project.id}
    />
  );
}
